import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Typography, IconButton, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ExpandMore, ExpandLess, Code } from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { 
  vscDarkPlus, 
  vs 
} from 'react-syntax-highlighter/dist/esm/styles/prism';

const StyledMarkdown = styled(Box)(({ theme }) => ({
  '& h1': {
    ...theme.typography.h3,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  '& h2': {
    ...theme.typography.h4,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2.5),
    color: theme.palette.mode === 'dark' ? '#BB86FC' : '#7C3AED',
    fontWeight: '600',
  },
  '& h3': {
    ...theme.typography.h5,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    color: theme.palette.mode === 'dark' ? '#4ADE80' : '#059669',
    fontWeight: '600',
  },
  '& h4': {
    ...theme.typography.h6,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    color: theme.palette.mode === 'dark' ? '#F87171' : '#DC2626',
    fontWeight: '600',
  },
  '& h5': {
    ...theme.typography.subtitle1,
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(1.5),
    color: theme.palette.mode === 'dark' ? '#FBBF24' : '#EA580C',
    fontWeight: '600',
  },
  '& h6': {
    ...theme.typography.subtitle2,
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    color: theme.palette.mode === 'dark' ? '#A78BFA' : '#9333EA',
    fontWeight: '600',
  },
  '& p': {
    ...theme.typography.body1,
    marginBottom: theme.spacing(2),
    lineHeight: 1.7,
    color: theme.palette.text.primary,
  },
  '& ul, & ol': {
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
  },
  '& li': {
    ...theme.typography.body1,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(2),
    margin: theme.spacing(2, 0),
    fontStyle: 'italic',
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(100, 181, 246, 0.08)' 
      : 'rgba(37, 99, 235, 0.05)',
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },
  '& code': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : '#F3F4F6',
    color: theme.palette.mode === 'dark' 
      ? '#BB86FC' 
      : '#7C3AED',
    padding: theme.spacing(0.25, 0.5),
    borderRadius: theme.spacing(0.5),
    fontFamily: 'monospace',
    fontSize: '0.875em',
    fontWeight: '500',
  },
  '& pre': {
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    border: theme.palette.mode === 'dark' 
      ? '1px solid rgba(255, 255, 255, 0.1)' 
      : '1px solid rgba(0, 0, 0, 0.1)',
    '& code': {
      backgroundColor: 'transparent !important',
      color: 'inherit !important',
      padding: '0 !important',
    },
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  '& hr': {
    border: 'none',
    borderTop: `2px solid ${theme.palette.divider}`,
    margin: theme.spacing(3, 0),
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: '500',
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.primary.dark,
    },
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(0.5),
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
  },
  '& th': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : '#F3F4F6',
    color: theme.palette.text.primary,
    padding: theme.spacing(1.5),
    textAlign: 'left',
    fontWeight: '600',
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  '& td': {
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
  },
  '& tr:nth-of-type(even) td': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.02)' 
      : '#F9FAFB',
  },
  '& tr:hover td': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : '#F3F4F6',
  },
  '& strong': {
    color: theme.palette.text.primary,
    fontWeight: '700',
  },
  '& em': {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

const CollapsibleCodeBlock = ({ language, children, theme, ...props }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  
  const codeContent = String(children).replace(/\n$/, '');
  const lineCount = codeContent.split('\n').length;
  
  return (
    <Box
      sx={{
        border: theme.palette.mode === 'dark' 
          ? '1px solid rgba(255, 255, 255, 0.1)' 
          : '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        marginBottom: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px',
          backgroundColor: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)' 
            : '#f5f5f5',
          borderBottom: theme.palette.mode === 'dark' 
            ? '1px solid rgba(255, 255, 255, 0.1)' 
            : '1px solid rgba(0, 0, 0, 0.1)',
          cursor: 'pointer',
        }}
        onClick={toggleExpanded}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Code 
            sx={{ 
              fontSize: '1rem', 
              color: theme.palette.mode === 'dark' ? '#BB86FC' : '#7C3AED' 
            }} 
          />
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: '500',
              color: theme.palette.text.primary,
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              letterSpacing: '0.5px'
            }}
          >
            {language} ({lineCount} lines)
          </Typography>
        </Box>
        <IconButton 
          size="small" 
          sx={{ 
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'rgba(0, 0, 0, 0.1)'
            }
          }}
        >
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      <Collapse in={isExpanded}>
        <SyntaxHighlighter
          style={theme.palette.mode === 'dark' ? vscDarkPlus : vs}
          language={language}
          PreTag="div"
          customStyle={{
            margin: 0,
            borderRadius: 0,
            fontSize: '0.875rem',
            lineHeight: '1.5',
            padding: '1.5rem',
            backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f8f8f8',
          }}
          codeTagProps={{
            style: {
              fontFamily: '"Fira Code", "JetBrains Mono", "Cascadia Code", "SF Mono", Consolas, monospace',
              fontSize: 'inherit',
            }
          }}
          {...props}
        >
          {codeContent}
        </SyntaxHighlighter>
      </Collapse>
    </Box>
  );
};

const MarkdownRenderer = ({ content, ...props }) => {
  const theme = props.theme || {
    palette: {
      mode: 'light',
      primary: {
        main: '#1976d2',
        dark: '#115293'
      },
      text: {
        primary: '#000000',
        secondary: '#666666'
      }
    }
  };
  
  const customRenderers = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      
      if (!inline && language) {
        return (
          <CollapsibleCodeBlock
            language={language}
            theme={theme}
            {...props}
          >
            {children}
          </CollapsibleCodeBlock>
        );
      }
      
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
  };

  return (
    <StyledMarkdown {...props}>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={customRenderers}
      >
        {content}
      </ReactMarkdown>
    </StyledMarkdown>
  );
};

export default MarkdownRenderer;